import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Sparkles, Beaker, Brain, Globe2, BookOpen, Lock, Store } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const HERO_IMG = "https://images.unsplash.com/photo-1733660227163-01bc46e0d7d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwZXJmdW1lJTIwYm90dGxlJTIwZGFyayUyMGx1eHVyeSUyMGNoYW1wYWduZSUyMGdvbGQlMjBwYWNrYWdpbmd8ZW58MHx8fHwxNzg5MTkyODI1fDA&ixlib=rb-4.1.0&q=85";
const AI_IMG = "https://images.unsplash.com/photo-1770301410072-f6ef6dad65b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBwZXJmdW1lJTIwYm90dGxlJTIwZGFyayUyMGx1eHVyeSUyMGNoYW1wYWduZSUyMGdvbGQlMjBwYWNrYWdpbmd8ZW58MHx8fHwxNzg5MTkyODI1fDA&ixlib=rb-4.1.0&q=85";
const TECH_IMG = "https://images.unsplash.com/photo-1709662217618-83586dc9b777?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxwZXJmdW1lJTIwbGFib3JhdG9yeSUyMEFJJTIwZnJhZ3JhbmNlJTIwdGVjaG5vbG9neSUyMG1vZGVybiUyMGNoZW1pc3RyeXxlbnwwfHx8fDE3ODkxOTI4MjV8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { tr } = useLang();
  return (
    <div className="min-h-screen">
      <Header transparent />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pfi-dotgrid opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#d4af37]/10 blur-[130px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 pfi-fade-up">
            <div className="pfi-overline mb-6">{tr("hero_tagline")}</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              PERSONA <span className="pfi-gradient-text">FRAGRANCE</span><br />
              INTELLIGENCE
            </h1>
            <p className="mt-8 text-lg text-[#a9a9b8] max-w-xl font-light leading-relaxed">
              {tr("hero_headline_a")} {tr("hero_headline_b")}{" "}
              <span className="text-[#e6c687]">{tr("hero_headline_c")}</span>{" "}
              {tr("hero_headline_d")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="pfi-btn-gold px-6 py-3.5 rounded-full text-sm inline-flex items-center justify-center gap-2"
              >
                {tr("hero_cta_primary")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/register"
                data-testid="hero-cta-secondary"
                className="pfi-btn-outline px-6 py-3.5 rounded-full text-sm inline-flex items-center justify-center gap-2"
              >
                {tr("hero_cta_secondary")}
              </Link>
            </div>
            <div className="mt-10 inline-flex items-center gap-2 pfi-overline">
              <Globe2 size={14} className="text-[#d4af37]" /> {tr("heritage_badge")}
            </div>
          </div>
          <div className="lg:col-span-5 pfi-fade-up">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#d4af37]/10 via-transparent to-[#8B5CF6]/10 rounded-3xl blur-2xl" />
              <div className="relative pfi-glass rounded-3xl overflow-hidden aspect-[4/5]">
                <img src={HERO_IMG} alt="Persona luxury bottle" className="absolute inset-0 w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="pfi-overline">Consultation Engine · v1</div>
                  <div className="mt-2 font-serif text-2xl text-[#f8f8fc]">The Intelligence of Scent</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-overline">{tr("what_is_eyebrow")}</div>
        <div className="mt-4 grid lg:grid-cols-12 gap-10 items-end">
          <h2 className="lg:col-span-7 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#f8f8fc]">
            {tr("what_is_title")}
          </h2>
          <p className="lg:col-span-5 text-[#a9a9b8] text-lg leading-relaxed">{tr("what_is_body")}</p>
        </div>
      </section>

      {/* Two Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline">{tr("products_eyebrow")}</div>
        <h2 className="mt-4 font-serif text-3xl lg:text-5xl max-w-3xl">{tr("products_title")}</h2>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <ProductCard
            testid="landing-product-ai"
            title="PERSONA AI PERFUMER"
            subtitle={tr("product_ai_subtitle")}
            desc={tr("product_ai_desc")}
            cta={tr("product_ai_cta")}
            img={AI_IMG}
            icon={<Brain size={18} />}
            to="/register"
          />
          <ProductCard
            testid="landing-product-tech"
            title="PERSONA PERFUMERY TECH"
            subtitle={tr("product_tech_subtitle")}
            desc={tr("product_tech_desc")}
            cta={tr("product_tech_cta")}
            img={TECH_IMG}
            icon={<Beaker size={18} />}
            to="/register"
          />
        </div>
      </section>

      {/* Ecosystem visual */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-overline">{tr("eco_eyebrow")}</div>
        <h2 className="mt-4 font-serif text-3xl lg:text-5xl">{tr("eco_title")}</h2>
        <div className="mt-12 pfi-card p-6 sm:p-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 pfi-overline">
              <Sparkles size={12} className="text-[#e6c687]" /> PERSONA FRAGRANCE INTELLIGENCE
            </div>
            <div className="mt-3 mx-auto h-10 w-px bg-gradient-to-b from-[#d4af37]/60 to-transparent" />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <EcoColumn
              title="PERSONA AI PERFUMER"
              role={tr("eco_ai_role")}
              rows={[tr("eco_ai_kb"), tr("eco_ai_cons"), tr("eco_ai_rec")]}
              accent="from-[#e6c687]/20 to-transparent"
            />
            <EcoColumn
              title="PERSONA PERFUMERY TECH"
              role={tr("eco_tech_role")}
              rows={[tr("eco_tech_core"), tr("eco_tech_res"), tr("eco_tech_form")]}
              accent="from-[#8B5CF6]/20 to-transparent"
            />
          </div>
          <p className="mt-8 text-center text-sm text-[#a9a9b8] font-mono tracking-wide">
            {tr("eco_core_note")}
          </p>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="pfi-overline">{tr("why_eyebrow")}</div>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl">{tr("why_title")}</h2>
            <p className="mt-6 text-[#a9a9b8] leading-relaxed">{tr("why_body")}</p>
          </div>
          <div className="lg:col-span-6">
            <div className="pfi-overline mb-4">{tr("ai_eyebrow")}</div>
            <h3 className="font-serif text-2xl lg:text-3xl">{tr("ai_title")}</h3>
            <ul className="mt-6 space-y-3 text-[#c8c8d4]">
              {[tr("ai_bullet_1"), tr("ai_bullet_2"), tr("ai_bullet_3"), tr("ai_bullet_4")].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Four ecosystem paths (LEARN / EXPLORE / CREATE / CRAFT & BUSINESS) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline">{tr("eco_eyebrow")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl" data-testid="four-paths-title">{tr("eco4_title")}</h2>
        <p className="mt-4 text-[#a9a9b8] max-w-2xl">{tr("eco4_sub")}</p>
        <p className="mt-2 text-sm text-[#6c6c7a] max-w-2xl">{tr("eco4_support")}</p>

        <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          <PathCard
            testid="path-learn"
            pathNo={tr("path_learn_no")}
            eyebrow={tr("path_learn")}
            title={tr("path_learn_title")}
            sub={tr("path_learn_sub")}
            desc={tr("path_learn_desc")}
            cta={tr("path_learn_cta")}
            to="/academy"
            icon={<BookOpen size={14} />}
          />
          <PathCard
            testid="path-explore"
            pathNo={tr("path_explore_no")}
            eyebrow={tr("path_explore")}
            title={tr("path_explore_title")}
            sub={tr("path_explore_sub")}
            desc={tr("path_explore_desc")}
            cta={tr("path_explore_cta")}
            to="/ai-perfumer"
            icon={<Brain size={14} />}
            accent
          />
          <PathCard
            testid="path-create"
            pathNo={tr("path_create_no")}
            eyebrow={tr("path_create")}
            title={tr("path_create_title")}
            sub={tr("path_create_sub")}
            desc={tr("path_create_desc")}
            cta={tr("path_create_cta")}
            to="/perfumery-tech"
            icon={<Beaker size={14} />}
            note={tr("path_create_note")}
          />
          <PathCard
            testid="path-craft"
            pathNo={tr("path_craft_no")}
            eyebrow={tr("path_craft")}
            title={tr("path_craft_title")}
            sub={tr("path_craft_sub")}
            desc={tr("path_craft_desc")}
            cta={tr("path_craft_cta")}
            to="/twfh"
            icon={<Store size={14} />}
          />
        </div>
      </section>

      {/* Heritage teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-card overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 lg:p-12">
              <div className="pfi-overline">{tr("heritage_eyebrow")}</div>
              <h3 className="mt-3 font-serif text-3xl lg:text-4xl">{tr("heritage_title")}</h3>
              <p className="mt-4 text-[#a9a9b8] leading-relaxed">{tr("heritage_body")}</p>
              <Link
                to="/journey"
                data-testid="landing-heritage-cta"
                className="mt-6 pfi-btn-outline rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2"
              >
                {tr("heritage_cta")} <ArrowRight size={12} />
              </Link>
            </div>
            <div className="relative bg-[#0e0e12] min-h-[280px]">
              <img
                src={ASSETS.founderPortrait}
                alt="Tubagus Wijaya"
                className="absolute inset-0 h-full w-full object-cover object-top opacity-95"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e12] via-transparent to-transparent md:block hidden" />
            </div>
          </div>
        </div>
      </section>

      {/* Protected Technology */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline">{tr("protected_eyebrow")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">{tr("protected_title")}</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <ProtectedTile title={tr("protected_persona")} desc={tr("protected_persona_desc")} />
          <ProtectedTile title={tr("protected_software")} desc={tr("protected_software_desc")} />
          <ProtectedTile title={tr("protected_core")} desc={tr("protected_core_desc")} highlight icon={<Lock size={14} />} />
        </div>
      </section>

      {/* TWFH — path 04 preview strip (linking to full TWFH page) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-card overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="relative bg-black flex items-center justify-center p-10 min-h-[260px]">
              <img src={ASSETS.twfhLogo} alt="TWFH" className="max-h-[280px] w-auto object-contain" />
            </div>
            <div className="p-8 lg:p-12">
              <div className="pfi-overline">{tr("path_craft_no")} · {tr("path_craft")}</div>
              <h3 className="mt-3 font-serif text-3xl">{tr("path_craft_title")}</h3>
              <div className="mt-1 font-serif text-lg text-[#e6c687]">{tr("twfh_page_subtitle")}</div>
              <div className="mt-3 text-xs font-mono tracking-wider text-[#a9a9b8]">{tr("path_craft_sub")}</div>
              <p className="mt-6 text-[#a9a9b8] leading-relaxed">{tr("path_craft_desc")}</p>
              <Link
                to="/twfh"
                data-testid="landing-twfh-cta"
                className="mt-6 pfi-btn-outline rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2"
              >
                {tr("path_craft_cta")} <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Membership (configuring) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-overline">{tr("membership_eyebrow")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">{tr("membership_title")}</h2>
        <p className="mt-4 text-[#a9a9b8] max-w-2xl leading-relaxed">{tr("membership_body")}</p>
        <div className="mt-10 pfi-card p-8" data-testid="membership-configuring">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              tr("membership_benefit_ai"),
              tr("membership_benefit_academy"),
              tr("membership_benefit_profile"),
              tr("membership_benefit_history"),
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3 border border-white/5">
                <span className="h-6 w-6 rounded-full grid place-items-center bg-[#d4af37]/15 text-[#e6c687] text-xs">✓</span>
                <span className="text-sm text-[#f8f8fc]">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <h2 className="font-serif text-3xl lg:text-4xl">{tr("faq_title")}</h2>
        <div className="mt-8">
          <Accordion type="single" collapsible className="w-full">
            {[
              [tr("faq_q1"), tr("faq_a1")],
              [tr("faq_q2"), tr("faq_a2")],
              [tr("faq_q3"), tr("faq_a3")],
              [tr("faq_q4"), tr("faq_a4")],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-white/5">
                <AccordionTrigger className="text-left text-[#f8f8fc] hover:text-[#e6c687] font-serif text-lg">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-[#a9a9b8] leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-card p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute inset-0 pfi-shimmer opacity-[0.06]" />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl">{tr("final_cta_title")}</h2>
              <p className="mt-3 text-[#a9a9b8] max-w-xl">{tr("final_cta_sub")}</p>
            </div>
            <Link
              to="/register"
              data-testid="final-cta-btn"
              className="pfi-btn-gold px-6 py-3.5 rounded-full text-sm inline-flex items-center gap-2"
            >
              {tr("final_cta_button")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProductCard({ title, subtitle, desc, cta, to, img, icon, testid }) {
  return (
    <div className="pfi-card overflow-hidden group" data-testid={testid}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/40 to-transparent" />
        <div className="absolute top-4 left-4 pfi-overline inline-flex items-center gap-2 bg-[#0a0a0c]/70 px-3 py-1 rounded-full">
          {icon} {subtitle}
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <div className="font-serif text-2xl">{title}</div>
        <p className="mt-3 text-[#a9a9b8] leading-relaxed text-sm">{desc}</p>
        <Link to={to} className="mt-6 inline-flex items-center gap-2 text-[#e6c687] hover:text-[#f8f8fc] text-sm">
          {cta} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

function EcoColumn({ title, role, rows, accent }) {
  return (
    <div className={`relative rounded-2xl p-6 border border-white/5 bg-gradient-to-b ${accent}`}>
      <div className="pfi-overline">{role}</div>
      <div className="mt-2 font-serif text-xl">{title}</div>
      <div className="mt-6 space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 text-sm text-[#c8c8d4]">
            <span className="h-1 w-6 bg-[#d4af37]/60 rounded-full" />
            <span>{r}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PathCard({ pathNo, eyebrow, title, sub, desc, cta, to, icon, accent, note, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={
        "pfi-card p-6 sm:p-7 group flex flex-col h-full " +
        (accent ? "border-[#d4af37]/40" : "")
      }
    >
      <div className="flex items-center justify-between">
        <div className="pfi-overline inline-flex items-center gap-2">{icon}{eyebrow}</div>
        <div className="pfi-overline text-[10px] text-[#6c6c7a]">{pathNo}</div>
      </div>
      <div className="mt-4 font-serif text-xl text-[#f8f8fc]">{title}</div>
      <div className="mt-1 text-[11px] font-mono tracking-wider text-[#a9a9b8] uppercase">{sub}</div>
      <p className="mt-4 text-sm text-[#a9a9b8] leading-relaxed flex-1">{desc}</p>
      {note ? (
        <p className="mt-3 text-[11px] font-mono text-[#6c6c7a] leading-relaxed">{note}</p>
      ) : null}
      <div className="mt-6 inline-flex items-center gap-1 text-[#e6c687] text-xs group-hover:gap-2 transition-all">
        {cta} <ArrowRight size={12} />
      </div>
    </Link>
  );
}

function PillarCard({ icon, eyebrow, title, to, accent, testid }) {
  return (
    <Link to={to} data-testid={testid} className={"pfi-card p-8 group block " + (accent ? "border-[#d4af37]/30" : "")}>
      <div className="pfi-overline inline-flex items-center gap-2">{icon}{eyebrow}</div>
      <div className="mt-4 font-serif text-xl">{title}</div>
      <div className="mt-6 inline-flex items-center gap-1 text-[#e6c687] text-xs group-hover:gap-2 transition-all">
        Enter <ArrowRight size={12} />
      </div>
    </Link>
  );
}

function ProtectedTile({ title, desc, highlight, icon }) {
  return (
    <div className={"pfi-card p-6 " + (highlight ? "border-[#d4af37]/40" : "")}>
      <div className="pfi-overline inline-flex items-center gap-2">{icon}{desc}</div>
      <div className={"mt-3 font-serif text-xl " + (highlight ? "text-[#e6c687]" : "")}>{title}</div>
    </div>
  );
}
